import Promise from 'bluebird';
import { omit, flatten, first, find, result } from 'underscore';
import { useCallback, useContext, useMemo } from 'react';
import { useWizardService, getFlowFactor, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ActivityContext, ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import {
  loadCampaign,
  loadAdGroup,
  loadDsaTargets,
  getDsaDefaultWebsiteLanguage,
  defaultTargetingSource,
  dsaDefaultTextEnabled,
  saveCampaign,
  saveAdGroup,
  readLocationCriterions,
  readAdGroupLocationCriterions,
  loadBundleCountries,
  modelToViewModelForAdgroupLocationTargets,
  DSA_TARGET_TYPES,
  DSA_TARGET_SUB_TYPES,
  insertErrorsByClientIdAndGenerateKey,
} from '@bingads-webui-campaign-react/search-campaign-data';
import {
  useKeywordSuggestion,
} from '@bingads-webui-campaign-react/copilot-keyword-planner';
import { generateNameSuggestionSuffix } from '@bingads-webui-campaign/audience-campaign-shared';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { useKeywordPlannerService } from '@bingads-webui-campaign-react/keyword-planner-shared';
import { handleKeywordSuggestionsWhenChanges, useSetKeywordSuggestionsData } from '../../shared/keyword-suggestion-util';
import { AD_GROUP_TYPES } from '../step-utils';

const defaultWebsites = [];

const defaultDsaTargets = {
  [DSA_TARGET_TYPES.categoryTargets]: {
    disabled: false,
    selected: false,
    items: [],
    savedItems: {},
  },
  [DSA_TARGET_TYPES.allTargets]: {
    disabled: false,
    selected: false,
    items: [],
    savedItems: {},
  },
  [DSA_TARGET_TYPES.specificTargets]: {
    disabled: false,
    selected: false,
    [DSA_TARGET_SUB_TYPES.exactUrls]: {
      items: [],
      savedItems: {},
    },
    [DSA_TARGET_SUB_TYPES.rules]: {
      items: [],
      savedItems: {},
    },
  },
  [DSA_TARGET_TYPES.customLabelTargets]: {
    disabled: false,
    selected: false,
    items: [],
    savedItems: {},
  },
};

const defaultAdGroupSettings = {
  [AD_GROUP_TYPES.SearchStandard]: {
    AdGroupType: AD_GROUP_TYPES.SearchStandard,
    websites: defaultWebsites,
  },
  [AD_GROUP_TYPES.SearchDynamic]: {
    AdGroupType: AD_GROUP_TYPES.SearchDynamic,
  },
};

export const useAdGroupSettingsRepository = () => {
  const i18n = useContext(I18nContext);
  const scenario = useContext(ScenarioContext);
  const activity = useContext(ActivityContext);
  const { scope } = useContext(ScopeContext) || {};
  const serverContext = useContext(ServerContext);
  const { cid, aid, campaignId } = scope.values;

  const {
    DynamicPermissions,
    Permissions,
  } = serverContext;
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);

  const defaultStandardName = generateNameSuggestionSuffix('Search-Standard-AdGroup');
  const defaultDynamicName = generateNameSuggestionSuffix('Search-Dynamic-AdGroup');

  const { state, send } = useWizardService();
  const { adGroupId, websites: savedWebsites } = state.context.wizardData;
  const { onRequest, onSuccess, onFailed } = useSetKeywordSuggestionsData({ send, permissions });
  const { getSearchIdea } = useKeywordPlannerService();

  const load = useCallback(() => {
    const dsaDefaultWebsiteLanguage = getDsaDefaultWebsiteLanguage(permissions);

    const defaultSearchStandard = {
      ...defaultAdGroupSettings[AD_GROUP_TYPES.SearchStandard],
      Name: defaultStandardName,
    };

    const defaultSearchDynamic = {
      ...defaultAdGroupSettings[AD_GROUP_TYPES.SearchDynamic],
      Name: defaultDynamicName,
      websites: defaultWebsites,
      selectedWebsiteLanguage: dsaDefaultWebsiteLanguage,
      selectedTargetingSource: defaultTargetingSource,
      dynamicTextEnabled: dsaDefaultTextEnabled,
      DsaTargets: cloneDeep(defaultDsaTargets),
    };

    const loadPromises = {
      campaign: loadCampaign(cid, aid, campaignId),
      adGroup: adGroupId ? loadAdGroup(cid, aid, campaignId, adGroupId) : Promise.resolve({
        ...defaultAdGroupSettings,
        [AD_GROUP_TYPES.SearchStandard]: defaultSearchStandard,
        [AD_GROUP_TYPES.SearchDynamic]: defaultSearchDynamic,
      }),
      campaignLocationTargets: readLocationCriterions(cid, aid, campaignId),
      adgroupLocationTargets: adGroupId ? readAdGroupLocationCriterions(cid, aid, campaignId, adGroupId) : Promise.resolve({}),
    };

    return Promise.props(loadPromises)
      .then(async ({
        campaign,
        adGroup,
        campaignLocationTargets,
        adgroupLocationTargets,
      }) => {
        let loadedAdGroup = adGroup;
        const bundleCountriesAndLocationCriterions = await loadBundleCountries({
          cid, aid, serverContext, language: campaign.Languages,
        });

        const { CampaignSettings } = campaign;
        const dsaSettings = find(CampaignSettings, setting => setting['@odata.type'] === '#Model.DynamicSearchAdsSetting') || first(CampaignSettings);
        const hasDSASettingsInCampaignSettings = result(dsaSettings, 'DomainName') !== undefined;

        if (adGroupId) {
          const { AdGroupType } = loadedAdGroup;
          if (AdGroupType === AD_GROUP_TYPES.SearchStandard) {
            loadedAdGroup = {
              [AD_GROUP_TYPES.SearchStandard]: loadedAdGroup,
              [AD_GROUP_TYPES.SearchDynamic]: defaultSearchDynamic,
            };
          } else {
            const dsaViewModel = await loadDsaTargets(cid, aid, campaignId, adGroupId);

            loadedAdGroup = {
              [AD_GROUP_TYPES.SearchStandard]: defaultSearchStandard,
              [AD_GROUP_TYPES.SearchDynamic]: {
                ...loadedAdGroup,
                DsaTargets: dsaViewModel,
              },
            };
          }
        }

        loadedAdGroup = {
          ...loadedAdGroup,
          [AD_GROUP_TYPES.SearchStandard]: {
            ...loadedAdGroup[AD_GROUP_TYPES.SearchStandard],
            websites: savedWebsites || defaultWebsites,
            allowAdGroupTypeChange: !adGroupId,
          },
          [AD_GROUP_TYPES.SearchDynamic]: {
            ...loadedAdGroup[AD_GROUP_TYPES.SearchDynamic],
            showDsaSettings: !hasDSASettingsInCampaignSettings,
            allowEditDynamicAdTarget: !adGroupId,
            websites: hasDSASettingsInCampaignSettings ? [dsaSettings.DomainName] : defaultWebsites,
            selectedWebsiteLanguage: result(dsaSettings, 'Language', dsaDefaultWebsiteLanguage),
            selectedTargetingSource: result(dsaSettings, 'Source', defaultTargetingSource),
            dynamicTextEnabled: result(dsaSettings, 'DynamicDescriptionEnabled', dsaDefaultTextEnabled),
            allowAdGroupTypeChange: !adGroupId,
          },
        };

        return Promise.resolve({
          campaign,
          adGroup: loadedAdGroup,
          ...(!adGroupId || adGroup.AdGroupType === AD_GROUP_TYPES.SearchStandard ? modelToViewModelForAdgroupLocationTargets({
            model: adgroupLocationTargets.value || [],
            campaignLocationModel: campaignLocationTargets.value,
            bundleCountries: bundleCountriesAndLocationCriterions.bundleCountries,
            adGroupId,
            campaignId,
          }) : {}),
          ...bundleCountriesAndLocationCriterions,
        });
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(`Load adGroup settings failed: ${error}`);
        activity.signal(`onAdGroupSettingsLoadFailed - ${JSON.stringify({
          campaignId,
          adGroupId,
          error,
        })}`);
        return Promise.reject(error);
      });
  }, [activity, adGroupId, aid, campaignId, cid, defaultDynamicName, defaultStandardName, permissions, savedWebsites, serverContext]);

  const recommendateRequest = useKeywordSuggestion();

  const save = useCallback(({ data }) => {
    const currentAdGroupType = getFlowFactor(state, 'adGroupType') || AD_GROUP_TYPES.SearchStandard;
    const subAdGroup = currentAdGroupType === AD_GROUP_TYPES.SearchStandard ? data.adGroup[AD_GROUP_TYPES.SearchStandard] : data.adGroup[AD_GROUP_TYPES.SearchDynamic];
    const {
      locationTargets,
      campaign,
      bundleLocationCriterions,
    } = data;

    if (currentAdGroupType === AD_GROUP_TYPES.SearchStandard) {
      const currentLanguages = subAdGroup.Language === 'None' || !subAdGroup.Language ? campaign.Languages : [subAdGroup.Language];
      const currentLocationSettings = locationTargets.useParentLevel ? locationTargets.parentLevelSettings : locationTargets.currentLevelSettings;
      handleKeywordSuggestionsWhenChanges({
        currentUrl: subAdGroup && first(subAdGroup.websites),
        currentLocationSettings,
        currentLanguages,
        previousUrl: state.context.wizardData.previousUrl || [],
        previousLocation: state.context.wizardData.previousLocation || [],
        previousLanguages: state.context.wizardData.previousLanguages || [],
        activity,
        bundleLocationCriterions,
        onRequest,
        onSuccess,
        onFailed,
        recommendateRequest,
        getSearchIdea,
        send,
        permissions,
      });
    }

    if (adGroupId) {
      subAdGroup.Id = adGroupId;
    }

    const saveAdGroupPromise = () => saveAdGroup({
      adGroup: subAdGroup,
      campaign: data.campaign,
    }, {
      permissions,
      scenario: scenario.name,
      i18n,
      activityLogger: activity,
    });

    const saveCampaignPromise = () => saveCampaign({
      campaign: {
        Id: campaignId,
        hasDsaSettings: true,
        websites: subAdGroup.websites,
        selectedWebsiteLanguage: subAdGroup.selectedWebsiteLanguage,
        selectedTargetingSource: subAdGroup.selectedTargetingSource,
        dynamicTextEnabled: subAdGroup.dynamicTextEnabled,
        pageFeeds: subAdGroup.pageFeeds,
      },
      permissions,
    }, {
      scenario: scenario.name,
      i18n,
      activityLogger: activity,
    });

    const saveAdGroupPromiseWithErrorHandling = successCampaign => saveAdGroupPromise()
      .then((res) => {
        const adGroupResult = omit(res.adGroup, 'AdGroups');

        send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
          adGroupId: Number(adGroupResult.Id),
          domain: first(subAdGroup.websites),
          websites: subAdGroup.websites,
        });

        if (currentAdGroupType === AD_GROUP_TYPES.SearchStandard) {
          return Promise.resolve({
            adGroup: {
              [AD_GROUP_TYPES.SearchStandard]: {
                ...adGroupResult,
                websites: subAdGroup.websites,
              },
              [AD_GROUP_TYPES.SearchDynamic]: data.adGroup[AD_GROUP_TYPES.SearchDynamic],
            },
          });
        }
        return Promise.resolve({
          adGroup: {
            [AD_GROUP_TYPES.SearchStandard]: data.adGroup[AD_GROUP_TYPES.SearchStandard],
            [AD_GROUP_TYPES.SearchDynamic]: {
              websites: subAdGroup.websites,
              selectedWebsiteLanguage: subAdGroup.selectedWebsiteLanguage,
              ...adGroupResult,
            },
          },
        });
      })
      .catch((res) => {
        const {
          adGroup = {},
          serverErrors = [],
          systemErrors = [],
        } = res || {};

        if (adGroup.Id) {
          send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
            adGroupId: Number(adGroup.Id),
          });
        }

        let partialSuccessAdGroup = data.adGroup;

        if (currentAdGroupType === AD_GROUP_TYPES.SearchDynamic) {
          const targetPaths = [
            'categoryTargets',
            'allTargets',
            'specificTargets.exactUrls',
            'specificTargets.rules',
            'customLabelTargets',
          ];
          insertErrorsByClientIdAndGenerateKey(adGroup.DsaTargets, targetPaths, serverErrors);

          partialSuccessAdGroup = {
            ...partialSuccessAdGroup,
            [AD_GROUP_TYPES.SearchDynamic]: {
              ...partialSuccessAdGroup[AD_GROUP_TYPES.SearchDynamic],
              DsaTargets: adGroup.DsaTargets,
            },
          };

          const hasDSASettingsInCampaignSettings = result(successCampaign, 'DomainName') !== undefined;
          if (hasDSASettingsInCampaignSettings) {
            partialSuccessAdGroup = {
              ...partialSuccessAdGroup,
              [AD_GROUP_TYPES.SearchDynamic]: {
                ...partialSuccessAdGroup[AD_GROUP_TYPES.SearchDynamic],
                disableEditDsaSettings: hasDSASettingsInCampaignSettings,
                websites: [successCampaign.DomainName],
                selectedWebsiteLanguage: successCampaign.LanguageCode,
                selectedTargetingSource: successCampaign.Source,
                dynamicTextEnabled: successCampaign.DynamicDescriptionEnabled,
              },
            };
          }
        }

        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({
          adGroup: partialSuccessAdGroup,
          serverErrors,
          systemErrors,
        });
      });

    const saveCampaignAdGroup = () => {
      if (currentAdGroupType === AD_GROUP_TYPES.SearchDynamic && subAdGroup.showDsaSettings) {
        // try to save campaign dsa settings first
        return saveCampaignPromise()
          .then(({ campaign: successCampaign }) => saveAdGroupPromiseWithErrorHandling(successCampaign))
          .catch(({ adGroup, serverErrors = [], systemErrors = [] }) => {
            // eslint-disable-next-line no-console
            console.error(`Save campaign settings failed: ${serverErrors} ${systemErrors}`);
            activity.signal(`onCampaignSettingsSaveFailed - ${JSON.stringify({
              campaignId,
              adGroupType: currentAdGroupType,
              adGroup: subAdGroup,
              serverErrors,
              systemErrors,
            })}`);

            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject({
              data: {
                ...data,
                adGroup: adGroup || data.adGroup,
              },
              error: {
                serverErrors: flatten(serverErrors.filter(val => val !== null)),
                systemErrors: flatten(systemErrors.filter(val => val !== null)),
              },
            });
          });
      }
      return saveAdGroupPromiseWithErrorHandling()
        .catch(({ adGroup, serverErrors = [], systemErrors = [] }) => {
          // eslint-disable-next-line no-console
          console.error(`Save adGroup settings failed: ${serverErrors} ${systemErrors}`);
          activity.signal(`onAdGroupSettingsSaveFailed - ${JSON.stringify({
            adGroupType: currentAdGroupType,
            adGroup: subAdGroup,
            serverErrors,
            systemErrors,
          })}`);

          // eslint-disable-next-line prefer-promise-reject-errors
          return Promise.reject({
            data: {
              ...data,
              adGroup: adGroup || data.adGroup,
            },
            error: {
              serverErrors: flatten(serverErrors.filter(val => val !== null)),
              systemErrors: flatten(systemErrors.filter(val => val !== null)),
            },
          });
        });
    };

    return saveCampaignAdGroup();
  }, [activity, campaignId, i18n, onFailed, onRequest, onSuccess, recommendateRequest, scenario.name, send, state, adGroupId, permissions, getSearchIdea]);

  return {
    load,
    save,
  };
};
